import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n"; // Initialize i18n for internationalization
import App from "@/App";
import { initAnalytics } from "@/lib/analytics";
import { AppErrorBoundary } from "@/components/common/AppErrorBoundary";
import { installGlobalErrorGuards } from "@/errorGuards";

// 🔇 Global Ably error suppressor for harmless lifecycle noise
(function patchConsoleErrorForAbly() {
  if (typeof console === 'undefined' || console.__ablyPatched) return;

  const originalError = console.error;

  console.error = (...args) => {
    try {
      const [first] = args;

      // If first arg is an Error or error-like object
      const msg =
        (first && first.message) ||
        (typeof first === 'string' ? first : '');

      if (typeof msg === 'string') {
        const lower = msg.toLowerCase();

        const isAblyLifecycleEvent =
          lower.includes('attach request superseded by a subsequent detach request') ||
          lower.includes('detach request superseded by a subsequent attach request') ||
          lower.includes('attach request superseded') ||
          lower.includes('detach request superseded') ||
          lower.includes('connection to server unavailable') ||
          lower.includes('channel detached') ||
          lower.includes('channel is detached') ||
          lower.includes('channel has detached') ||
          lower.includes('channel failed') ||
          lower.includes('channel suspended');

        if (isAblyLifecycleEvent) {
          // Downgrade to a warning so React's error overlay doesn't freak out
          console.warn('[ABLY-SUPPRESSED]', msg);
          return;
        }
      }
    } catch (e) {
      // If anything goes wrong, fall back to original behavior
    }

    return originalError(...args);
  };

  console.__ablyPatched = true;
})();

// Install global error guards BEFORE React mounts (Ticket 39)
installGlobalErrorGuards();

// Initialize PostHog analytics if key is provided
const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
if (posthogKey) {
  initAnalytics(posthogKey);
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
);
